import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import {
  AddressInformation,
  getOrderByNumberQueryKey,
  ComponentQuantityData,
  transformOrderApiResponse,
  useFacilitateOrder,
  useRefacilitateOrder,
  FacilitateOrderData,
} from '@frontend/api';
import {
  useCreateAddOn,
  useCreateQuantity,
  useUpdateAddOn,
  useUpdateAddOnCell,
} from '@frontend/api';
import { Address, QUOTE_STATUS } from '@frontend/api';
import { Link, useToast } from '@frontend/components';
import { toMoment } from '@frontend/utils';

import {
  PAYMENT_METHOD,
  SHIPPING_GROUPING,
  SHIPPING_TYPE,
} from 'constants/all';

import { initEditOrder } from 'store/orders/actions';
import { getOrder, getOrderId } from 'store/orders/selectors';
import { getQuote } from 'store/quotes/selectors';
import { getGroupId } from 'store/session/selectors';

import history from 'utils/browserHistory';

import {
  BILLING_INFO_TYPES,
  DRAWER_MODES,
  SHIPPING_INFO_TYPES,
} from '../constants';
import { getShipsOnDateFromLeadTime } from '../utils';
import {
  addressValidationModal,
  createOrder,
  facilitateOrderDrawer,
} from './actions';
import { setOptions, selectOption, selectAddOn } from './actions';
import {
  selectNewlyAddedBillingAddressInformation,
  selectNewlyAddedShippingAddressInformation,
  setCustomerInfoStep,
  setEditBillingAddress,
  setEditShippingAddress,
} from './actions';
import {
  getSelectedOptions,
  getShippingAccount,
  getShippingRate,
  getShippingType,
  getPaymentMethod,
  getPurchaseOrderFile,
  getPurchaseOrderNumber,
  getWithConfirmation,
  getConfirmationEmail,
  getContactEmail,
  getCard,
  getStripeToken,
  getShippingInformation,
  getBillingInformation,
  getPointOfContact,
  getAdjustments,
  getAllAdditionalCharges,
  getQuoteItems,
  getAccount,
  getContact,
  getFacility,
  getBillingAddress,
  getBillingInformationType,
  getShippingInformationType,
  getDrawerMode,
  getOrderedQuoteItemIds,
} from './reducer';
import { getCustomerInfoStep } from './reducer';
import {
  convertBillingAddressToBillingInformation,
  convertFacilityToShippingInformation,
} from './utils';

export const useConfirmAddress = () => {
  const dispatch = useDispatch();
  const step = useSelector(getCustomerInfoStep);

  return (address: Address) => {
    dispatch(addressValidationModal.close());
    if (step === 'shipping_address') {
      dispatch(selectNewlyAddedShippingAddressInformation({ address }));
      dispatch(setCustomerInfoStep({ step: 'billing_address' }));
    } else {
      dispatch(selectNewlyAddedBillingAddressInformation({ address }));
      dispatch(
        setCustomerInfoStep({ step: 'shipping_options_and_pricing_summary' }),
      );
    }
  };
};

export const useEditAddress = () => {
  const dispatch = useDispatch();
  const step = useSelector(getCustomerInfoStep);

  return (address: Address) => {
    dispatch(addressValidationModal.close());
    if (step === 'shipping_address') {
      dispatch(setEditShippingAddress({ address }));
    } else {
      dispatch(setEditBillingAddress({ address }));
    }
  };
};

export const useCreateOrder = () => {
  const dispatch = useDispatch();
  const quoteItems = useSelector(getQuoteItems);
  const selectedOptions = useSelector(getSelectedOptions);
  const adjustmentsMap = useSelector(getAdjustments);
  const additionalCharges = useSelector(getAllAdditionalCharges);

  const { mutateAsync: createAdjustment } = useCreateQuantity({
    query: { isForFacilitatedOrder: true },
  });

  const { mutateAsync: createAdditionalCharge } = useCreateAddOn({
    query: { isForFacilitatedOrder: true },
  });

  const { mutateAsync: updateAdditionalCharge } = useUpdateAddOn({
    query: { isForFacilitatedOrder: true },
  });

  const { mutateAsync: updateAddOnCell } = useUpdateAddOnCell({
    query: { isForFacilitatedOrder: true },
  });

  return useCallback(async () => {
    const componentToQuoteItemMap = quoteItems.reduce((acc, quoteItem) => {
      acc[quoteItem.rootComponent.id] = quoteItem.id;
      return acc;
    }, {} as Record<number, number>);

    const quantitiesToCreate = quoteItems
      .map((quoteItem) => {
        if (adjustmentsMap[quoteItem.id]) {
          return {
            componentId: quoteItem.rootComponent.id,
            quantity: adjustmentsMap[quoteItem.id].quantity,
            unitPrice: adjustmentsMap[quoteItem.id].unitPrice,
            leadTime: adjustmentsMap[quoteItem.id].leadTime,
          };
        }
      })
      .filter(
        (
          data,
        ): data is {
          componentId: number;
          quantity: number;
          unitPrice: string;
          leadTime: number;
        } => data !== undefined,
      );
    let newQuantitiesMap: Record<number, ComponentQuantityData> = {};

    if (quantitiesToCreate.length > 0) {
      const adjustments = await Promise.all(
        quantitiesToCreate.map((data) =>
          createAdjustment({
            componentId: data.componentId,
            data: {
              quantity: Number(data.quantity),
              isForFacilitatedOrder: true,
              manualLeadTime: Number(data.leadTime),
              manualTotalUnitPrice: data.unitPrice,
            },
          }),
        ),
      );

      newQuantitiesMap = adjustments.reduce(
        (acc, adjustment) => ({
          ...acc,
          [componentToQuoteItemMap[adjustment.componentId]]: adjustment,
        }),
        newQuantitiesMap,
      );
    }

    const newAdditionalCharges = await Promise.all(
      quoteItems
        .filter((quoteItem) => additionalCharges[quoteItem.id])
        .flatMap((quoteItem) => {
          const selectedQuantity =
            newQuantitiesMap[quoteItem.id] ||
            selectedOptions[quoteItem.id].quantity;

          return Object.values(additionalCharges[quoteItem.id]).map(
            async (additionalCharge) => {
              const newAddOn = await createAdditionalCharge({
                componentId: quoteItem.rootComponent.id,
                data: {
                  name: additionalCharge.name,
                },
              });

              // The api does not support creating required add-ons
              await updateAdditionalCharge({
                addOnId: newAddOn.id,
                data: {
                  isRequired: true,
                },
              });

              const cellIndex = newAddOn.addOnCells.findIndex(
                (cell) => cell.quantityId === selectedQuantity.id,
              );
              const cellToUpdate = newAddOn.addOnCells[cellIndex];

              const updatedCell = await updateAddOnCell({
                addOnCellId: cellToUpdate.id,
                data: {
                  manualPrice: additionalCharge.price,
                },
              });

              newAddOn.addOnCells[cellIndex] = {
                ...cellToUpdate,
                ...updatedCell,
              };

              return { ...newAddOn, isRequired: true };
            },
          );
        }),
    );

    const newAddOnCells = newAdditionalCharges.flatMap(
      (addOn) => addOn.addOnCells,
    );
    const quantityIdToAdditionalChargeMap = newAddOnCells.reduce<
      Record<string, Record<string, any>>
    >((acc, cell) => {
      acc[cell.quantityId] ??= {};
      acc[cell.quantityId][cell.addOnId] = cell;
      return acc;
    }, {});

    for (const quoteItemId in selectedOptions) {
      const option = selectedOptions[quoteItemId];
      if (quantityIdToAdditionalChargeMap[option.quantity.id]) {
        const additionalChargeMap =
          quantityIdToAdditionalChargeMap[option.quantity.id];
        Object.values(additionalChargeMap).forEach((ac) => {
          dispatch(
            selectAddOn({ addOn: ac, quoteItemId: Number(quoteItemId) }),
          );
        });
      }
    }

    for (const quantity of Object.values(newQuantitiesMap)) {
      dispatch(
        selectOption({
          quoteItemId: componentToQuoteItemMap[quantity.componentId],
          quantity,
          addOns: quantityIdToAdditionalChargeMap[quantity.id] || {},
        }),
      );
    }

    const componentIdToAddOnsMap: Record<number, unknown[]> = {};
    const componentIdToAddOnCellsMap: Record<number, unknown[]> = {};

    newAdditionalCharges.forEach((addOn) => {
      const { addOnCells, ...addOnData } = addOn;
      componentIdToAddOnsMap[addOn.componentId] ??= [];
      componentIdToAddOnsMap[addOn.componentId].push(addOnData);
      addOnCells.forEach((cell) => {
        componentIdToAddOnCellsMap[addOn.componentId] ??= [];
        componentIdToAddOnCellsMap[addOn.componentId].push(cell);
      });
    });

    const nextQuoteItems = quoteItems.map((quoteItem) => {
      const newQuantity = newQuantitiesMap[quoteItem.id];
      const addOns = componentIdToAddOnsMap[quoteItem.rootComponent.id];
      const addOnCells = componentIdToAddOnCellsMap[quoteItem.rootComponent.id];
      return {
        ...quoteItem,
        rootComponent: {
          ...quoteItem.rootComponent,
          addOns: [...quoteItem.rootComponent.addOns, ...(addOns ?? [])],
          addOnCells: [
            ...quoteItem.rootComponent.addOnCells,
            ...(addOnCells ?? []),
          ],
          quantities: [
            ...quoteItem.rootComponent.quantities,
            ...(newQuantity ? [newQuantity] : []),
          ],
        },
      };
    });

    dispatch(setOptions.success({ quoteItems: nextQuoteItems }));
  }, [
    additionalCharges,
    adjustmentsMap,
    createAdditionalCharge,
    createAdjustment,
    dispatch,
    quoteItems,
    selectedOptions,
    updateAddOnCell,
    updateAdditionalCharge,
  ]);
};

const ERROR_CODES = {
  STRIPE_ERROR: 'stripe_error',
  PAYMENT_TERMS_NOT_FOUND: 'payment_terms_not_found',
  ERROR_PERSISTS_CONTACT_SUPPORT: 'error_persists_contact_support',
  VALIDATION_ERROR: 'invalid',
};

const STRIPE_ERROR_CODES = {
  CARD_DECLINED: 'card_declined',
};

const STRIPE_ERROR = {
  [STRIPE_ERROR_CODES.CARD_DECLINED]: {
    title: "Couldn't process payment",
    message: 'The credit card was declined',
  },
  fallback: {
    title: "Couldn't process payment",
    message:
      'Double check payment information. If the problem persists, contact support',
  },
};

const CREATE_ORDER_ERROR = {
  [ERROR_CODES.PAYMENT_TERMS_NOT_FOUND]: {
    // this shouldn't happen, was only possible to get into this state for a short time
    title: "Couldn't facilitate order",
    message: 'Set payment terms on the account associated with this order',
  },
  [ERROR_CODES.ERROR_PERSISTS_CONTACT_SUPPORT]: {
    // this happens if stripe token is empty when trying to do a credit card payment
    title: "Couldn't process payment",
    message:
      'Double check payment information. If the problem persists, contact support',
  },
  fallback: {
    title: "Couldn't facilitate order",
    message: 'Try again once. If the problem persists, contact support',
  },
};

const KNOWN_VALIDATION_ERROR_KEYS = [
  'shippingInfo',
  'billingInfo',
] as const satisfies readonly (keyof FacilitateOrderData)[];

type ValidationErrorKey = (typeof KNOWN_VALIDATION_ERROR_KEYS)[number];

const VALIDATION_ERROR = {
  shippingInfo: {
    title: "Couldn't facilitate order",
    message: "Invalid Ship To address for the customer's facility",
  },
  billingInfo: {
    title: "Couldn't facilitate order",
    message: 'Invalid Billing address for the customer',
  },
} as const satisfies Record<
  ValidationErrorKey,
  { title: string; message: string }
>;

function buildValidationError(extra: Record<string, any>) {
  const key = Object.keys(extra).find((key): key is ValidationErrorKey =>
    KNOWN_VALIDATION_ERROR_KEYS.includes(key as ValidationErrorKey),
  );
  if (key) {
    return VALIDATION_ERROR[key];
  }
  return CREATE_ORDER_ERROR.fallback;
}

function buildError(err: any) {
  if (err.message === ERROR_CODES.STRIPE_ERROR) {
    return STRIPE_ERROR[err.extra.code] ?? STRIPE_ERROR.fallback;
  }

  if (CREATE_ORDER_ERROR[err.message]) {
    return CREATE_ORDER_ERROR[err.message];
  }

  if (err.code === ERROR_CODES.VALIDATION_ERROR) {
    return buildValidationError(err.extra);
  }

  return CREATE_ORDER_ERROR.fallback;
}

export const useFacilitateOrderData = () => {
  const account = useSelector(getAccount);
  const contact = useSelector(getContact);
  const facility = useSelector(getFacility);
  const selectedOptions = useSelector(getSelectedOptions);
  const orderedQuoteItemIds = useSelector(getOrderedQuoteItemIds);
  const billingAddress = useSelector(getBillingAddress);
  const billingInformationType = useSelector(getBillingInformationType);
  const billingInformation = useSelector(getBillingInformation);
  const shippingAccount = useSelector(getShippingAccount);
  const shippingInformationType = useSelector(getShippingInformationType);
  const shippingInformation = useSelector(getShippingInformation);
  const shippingRate = useSelector(getShippingRate);
  const shippingType = useSelector(getShippingType);
  const paymentMethod = useSelector(getPaymentMethod);
  const purchaseOrderFile = useSelector(getPurchaseOrderFile);
  const purchaseOrderNumber = useSelector(getPurchaseOrderNumber);
  const withConfirmation = useSelector(getWithConfirmation);
  const confirmationEmail = useSelector(getConfirmationEmail);
  const pointOfContact = useSelector(getPointOfContact);
  const creditCard = useSelector(getCard);
  const cardStripeToken = useSelector(getStripeToken);

  return useMemo(() => {
    const selectedOptionsList = orderedQuoteItemIds
      .filter((quoteItemId) => selectedOptions[quoteItemId])
      .map((quoteItemId) => {
        const option = selectedOptions[quoteItemId];
        return {
          quoteItemId,
          expediteId: option.expedite?.id ?? null,
          quantityId: option.quantity.id,
          addOnCellIds: Object.values(option.addOns).map(
            (addOnCell) => addOnCell.id,
          ),
          manualLeadTime: option.manualLeadTime,
          shipsOn: option.shipsOn,
        };
      });

    const shippingInfo =
      shippingInformationType === SHIPPING_INFO_TYPES.FACILITY
        ? convertFacilityToShippingInformation(account!, contact!, facility!)
        : shippingInformationType === SHIPPING_INFO_TYPES.ONE_TIME_ADDRESS
        ? shippingInformation
        : null;

    const billingInfo =
      billingInformationType === BILLING_INFO_TYPES.BILLING_ADDRESS
        ? convertBillingAddressToBillingInformation(
            account!,
            contact!,
            billingAddress!,
          )
        : billingInformationType === BILLING_INFO_TYPES.ONE_TIME_ADDRESS
        ? billingInformation
        : null;

    const stripeToken =
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD
        ? cardStripeToken
        : undefined;
    const card =
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD ? creditCard : undefined;
    const poFile = purchaseOrderFile || undefined;
    const poNumber =
      paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER
        ? purchaseOrderNumber
        : undefined;

    let shippingOption = shippingType
      ? {
          type: shippingType,
          shipWhen: SHIPPING_GROUPING.BULK,
        }
      : null;
    let shippingRateToken: string | undefined = undefined;
    if (shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT) {
      shippingOption = {
        ...shippingOption,
        ...shippingAccount,
      };
    } else if (shippingRate && shippingOption) {
      shippingOption = {
        ...shippingOption,
        shippingMethod: shippingRate.method,
      };
      // Only pass a shipping rate token when we're actually charging shipping now (credit card).
      // For purchase orders, the backend should not receive a rate token.
      if (paymentMethod === PAYMENT_METHOD.CREDIT_CARD && shippingRate.token) {
        shippingRateToken = shippingRate.token;
      }
    }

    return {
      selectedOptionsList,
      shippingInfo,
      billingInfo,
      paymentMethod: paymentMethod ?? null,
      stripeToken,
      card,
      poFile,
      poNumber,
      pointOfContact: pointOfContact || null,
      confirmationEmail: confirmationEmail || null,
      shippingOption,
      shippingRateToken,
      withConfirmation,
    };
  }, [
    account,
    billingAddress,
    billingInformationType,
    billingInformation,
    cardStripeToken,
    confirmationEmail,
    contact,
    creditCard,
    facility,
    orderedQuoteItemIds,
    paymentMethod,
    pointOfContact,
    purchaseOrderFile,
    purchaseOrderNumber,
    selectedOptions,
    shippingAccount,
    shippingInformationType,
    shippingInformation,
    shippingRate,
    shippingType,
    withConfirmation,
  ]);
};

export const useFacilitateOrderDataV2 = () => {
  const selectedOptions = useSelector(getSelectedOptions);
  const orderedQuoteItemIds = useSelector(getOrderedQuoteItemIds);
  const billingInfo = useSelector(getBillingInformation);
  const shippingAccount = useSelector(getShippingAccount);
  const shippingInfo = useSelector(getShippingInformation);
  const shippingRate = useSelector(getShippingRate);
  const shippingType = useSelector(getShippingType);
  const paymentMethod = useSelector(getPaymentMethod);
  const purchaseOrderFile = useSelector(getPurchaseOrderFile);
  const purchaseOrderNumber = useSelector(getPurchaseOrderNumber);
  const withConfirmation = useSelector(getWithConfirmation);
  const confirmationEmail = useSelector(getConfirmationEmail);
  const pointOfContact = useSelector(getPointOfContact);
  const creditCard = useSelector(getCard);
  const cardStripeToken = useSelector(getStripeToken);
  const drawerMode = useSelector(getDrawerMode);
  const order = useSelector(getOrder);
  const orderCreatedDate =
    drawerMode === DRAWER_MODES.EDIT && order ? order.created : undefined;

  return useMemo(() => {
    const selectedOptionsList = orderedQuoteItemIds
      .filter((quoteItemId) => selectedOptions[quoteItemId])
      .map((quoteItemId) => {
        const option = selectedOptions[quoteItemId];
        const leadTime =
          option.manualLeadTime ??
          option.expedite?.leadTime ??
          option.quantity.leadTime;
        const shipsOn =
          option.shipsOn ??
          getShipsOnDateFromLeadTime({
            fromDate: orderCreatedDate
              ? new Date(orderCreatedDate)
              : new Date(),
            leadTime,
            leadTimeDisplayUnit: option.quantity.leadTimeDisplayUnit,
          }).toISOString();

        return {
          quoteItemId,
          expediteId: option.expedite?.id ?? null,
          quantityId: option.quantity.id,
          addOnCellIds: Object.values(option.addOns).map(
            (addOnCell) => addOnCell.id,
          ),
          manualLeadTime: option.manualLeadTime,
          shipsOn: toMoment(shipsOn).format('YYYY-MM-DD'),
        };
      });

    const stripeToken =
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD
        ? cardStripeToken
        : undefined;
    const card =
      paymentMethod === PAYMENT_METHOD.CREDIT_CARD ? creditCard : undefined;
    const poFile = purchaseOrderFile || undefined;
    const poNumber =
      paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER
        ? purchaseOrderNumber
        : undefined;

    let shippingOption = shippingType
      ? {
          type: shippingType,
          shipWhen: SHIPPING_GROUPING.BULK,
        }
      : null;
    let shippingRateToken: string | undefined = undefined;
    if (shippingType === SHIPPING_TYPE.CUSTOMER_ACCOUNT) {
      shippingOption = {
        ...shippingOption,
        ...shippingAccount,
      };
    } else if (shippingRate && shippingOption) {
      shippingOption = {
        ...shippingOption,
        shippingMethod: shippingRate.method,
      };
      // Only pass a shipping rate token when we're actually charging shipping now (credit card).
      // For purchase orders, the backend should not receive a rate token.
      if (paymentMethod === PAYMENT_METHOD.CREDIT_CARD && shippingRate.token) {
        shippingRateToken = shippingRate.token;
      }
    }

    return {
      selectedOptionsList,
      shippingInfo,
      billingInfo,
      paymentMethod: paymentMethod ?? null,
      stripeToken,
      card,
      poFile,
      poNumber,
      pointOfContact,
      confirmationEmail,
      shippingOption,
      shippingRateToken,
      withConfirmation,
    };
  }, [
    billingInfo,
    cardStripeToken,
    confirmationEmail,
    creditCard,
    orderCreatedDate,
    orderedQuoteItemIds,
    paymentMethod,
    pointOfContact,
    purchaseOrderFile,
    purchaseOrderNumber,
    selectedOptions,
    shippingAccount,
    shippingInfo,
    shippingRate,
    shippingType,
    withConfirmation,
  ]);
};

export const useCompleteOrder = () => {
  const dispatch = useDispatch();
  const quote = useSelector(getQuote);
  const withConfirmation = useSelector(getWithConfirmation);
  const confirmationEmail = useSelector(getConfirmationEmail);
  const contactEmail = useSelector(getContactEmail);
  const facilitateOrderData = useFacilitateOrderData();

  const toast = useToast();

  const { mutateAsync: facilitateOrder } = useFacilitateOrder();

  return useCallback(async () => {
    if (!quote) {
      return;
    }
    try {
      dispatch(createOrder.request());

      const order = await facilitateOrder({
        quoteId: quote.id,
        data: {
          quoteNumber: quote.number,
          revisionNumber: quote.revisionNumber,
          ...facilitateOrderData,
        },
      });
      toast({
        title: `Order #${order.number} Facilitated`,
        status: 'success',
        description: (
          <>
            <Link
              color="blue"
              onClick={() => history.push(`/orders/edit/${order.number}`)}
            >
              Go To Order
            </Link>
            {withConfirmation
              ? ` Confirmation Sent: ${
                  contactEmail + `\n${confirmationEmail || ''}`
                }`
              : null}
          </>
        ),
      });
      history.push(`/quotes/${QUOTE_STATUS.ALL}`);
    } catch (error) {
      dispatch(
        createOrder.error({
          ...(error as Error),
          ...buildError(error),
        }),
      );
    }
  }, [
    dispatch,
    facilitateOrder,
    quote,
    facilitateOrderData,
    toast,
    withConfirmation,
    contactEmail,
    confirmationEmail,
  ]);
};

export const useCompleteOrderV2 = () => {
  const dispatch = useDispatch();
  const quote = useSelector(getQuote);
  const withConfirmation = useSelector(getWithConfirmation);
  const confirmationEmail = useSelector(getConfirmationEmail);
  const contactEmail = useSelector(getContactEmail);
  const facilitateOrderData = useFacilitateOrderDataV2();

  const toast = useToast();

  const { mutateAsync: facilitateOrder } = useFacilitateOrder({
    queryOptions: {
      onMutate: () => {
        dispatch(createOrder.request());
        return undefined;
      },
      onSuccess: (data) => {
        const order = transformOrderApiResponse(data);
        dispatch(createOrder.success());
        dispatch(facilitateOrderDrawer.close());
        history.push(`/quotes/${QUOTE_STATUS.ALL}`);
        toast({
          title: `Order #${order.number} Facilitated`,
          status: 'success',
          description: (
            <>
              <Link
                color="blue"
                onClick={() => history.push(`/orders/edit/${order.number}`)}
              >
                Go To Order
              </Link>
              {withConfirmation
                ? ` Confirmation Sent: ${
                    contactEmail + `\n${confirmationEmail || ''}`
                  }`
                : null}
            </>
          ),
        });
      },
      onError: (error) => {
        dispatch(
          createOrder.error({
            ...(error as Error),
            ...buildError(error),
          }),
        );
      },
    },
  });

  return useCallback(
    () =>
      facilitateOrder({
        quoteId: quote.id,
        data: {
          quoteNumber: quote.number,
          revisionNumber: quote.revisionNumber,
          ...facilitateOrderData,
        },
      }),
    [facilitateOrder, quote, facilitateOrderData],
  );
};

export const useCompleteRefacilitateOrder = () => {
  const dispatch = useDispatch();
  const orderId = useSelector(getOrderId);
  const toast = useToast();
  const groupId = useSelector(getGroupId);
  const queryClient = useQueryClient();
  const withConfirmation = useSelector(getWithConfirmation);
  const confirmationEmail = useSelector(getConfirmationEmail);
  const contactEmail = useSelector(getContactEmail);
  const { mutate: refacilitateOrder, isLoading } = useRefacilitateOrder(
    { orderId },
    {
      queryOptions: {
        onMutate: () => {
          dispatch(createOrder.request());
          return undefined;
        },
        onSuccess: (data) => {
          const order = transformOrderApiResponse(data);
          dispatch(createOrder.success());
          dispatch(facilitateOrderDrawer.close());
          queryClient.setQueryData(
            getOrderByNumberQueryKey({
              groupId,
              orderNumber: `${order.number}`,
            }),
            order,
          );
          dispatch(initEditOrder(order));
          toast({
            status: 'success',
            title: `Order #${order.number} Revised`,
            description: withConfirmation
              ? ` Confirmation Sent: ${
                  contactEmail + `\n${confirmationEmail || ''}`
                }`
              : null,
          });
        },
        onError: (error) => {
          dispatch(
            createOrder.error({
              ...(error as Error),
              ...buildError(error),
            }),
          );
        },
      },
    },
  );
  const facilitateOrderData = useFacilitateOrderDataV2();

  const refacilitateOrderCallback = useCallback(() => {
    const {
      selectedOptionsList,
      shippingInfo,
      billingInfo,
      poFile,
      poNumber,
      pointOfContact,
      confirmationEmail,
      shippingOption,
      withConfirmation,
    } = facilitateOrderData;
    refacilitateOrder({
      selectedOptionsList,
      shippingInfo,
      billingInfo,
      poFile,
      poNumber: poNumber ?? null,
      pointOfContact: pointOfContact || null,
      confirmationEmail: confirmationEmail || null,
      shippingOption,
      withConfirmation,
    });
  }, [facilitateOrderData, refacilitateOrder]);

  return {
    refacilitateOrder: refacilitateOrderCallback,
    isLoading,
  };
};

export type ConfirmAddressState =
  | {
      isSuggestedAddressModalOpen: false;
      isUnvalidatedAddressPopoverOpen: false;
      pendingAddress: null;
      suggestedAddresses: never[];
    }
  | {
      isSuggestedAddressModalOpen: true;
      isUnvalidatedAddressPopoverOpen: false;
      pendingAddress: AddressInformation;
      suggestedAddresses: AddressInformation[];
    }
  | {
      isSuggestedAddressModalOpen: false;
      isUnvalidatedAddressPopoverOpen: true;
      pendingAddress: AddressInformation;
      suggestedAddresses: never[];
    };

type UseConfirmAddressStateParams = {
  onConfirmAddress: (address: AddressInformation) => void;
};

export const useConfirmAddressState = ({
  onConfirmAddress,
}: UseConfirmAddressStateParams) => {
  const [confirmAddressState, setConfirmAddressState] =
    useState<ConfirmAddressState>({
      isSuggestedAddressModalOpen: false,
      isUnvalidatedAddressPopoverOpen: false,
      pendingAddress: null,
      suggestedAddresses: [],
    });

  const openSuggestedAddressModal = useCallback(
    ({
      pendingAddress,
      suggestedAddresses,
    }: {
      pendingAddress: AddressInformation;
      suggestedAddresses: AddressInformation[];
    }) => {
      setConfirmAddressState({
        isSuggestedAddressModalOpen: true,
        isUnvalidatedAddressPopoverOpen: false,
        pendingAddress,
        suggestedAddresses,
      });
    },
    [],
  );

  const openUnvalidatedAddressPopover = useCallback(
    ({ pendingAddress }: { pendingAddress: AddressInformation }) => {
      setConfirmAddressState({
        isSuggestedAddressModalOpen: false,
        isUnvalidatedAddressPopoverOpen: true,
        pendingAddress,
        suggestedAddresses: [],
      });
    },
    [],
  );

  const clearPendingAddress = useCallback(() => {
    setConfirmAddressState({
      isSuggestedAddressModalOpen: false,
      isUnvalidatedAddressPopoverOpen: false,
      pendingAddress: null,
      suggestedAddresses: [],
    });
  }, []);

  const confirmAddress = useCallback(
    (address: AddressInformation) => {
      onConfirmAddress(address);
      clearPendingAddress();
    },
    [clearPendingAddress, onConfirmAddress],
  );

  return {
    confirmAddressState,
    clearPendingAddress,
    confirmAddress,
    openSuggestedAddressModal,
    openUnvalidatedAddressPopover,
  };
};
